import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useUser } from '@/contexts/UserContext';
import { useHeader } from '@/contexts/HeaderContext';
import { supabase } from '@/lib/supabase';
import axios from 'axios';
import Loading from '../components/Loading';
import AchievementsSkeleton from '../components/skeletons/AchievementsSkeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Group labels ─── */
const GROUP_LABELS = {
  streak:      { en: 'Streak',           it: 'Serie',            es: 'Racha' },
  completions: { en: 'Completions',      it: 'Completamenti',    es: 'Completados' },
  mastery:     { en: 'Category Mastery', it: 'Padronanza Area',  es: 'Dominio de Categoría' },
  time:        { en: 'Time Invested',    it: 'Tempo Investito',  es: 'Tiempo Invertido' },
  forest:      { en: 'Forest',           it: 'Foresta',          es: 'Bosque' },
  engagement:  { en: 'Engagement',       it: 'Coinvolgimento',   es: 'Compromiso' },
};

const GROUP_ORDER = ['streak', 'completions', 'mastery', 'time', 'forest', 'engagement'];

/* ─── Duolingo-style badge colors per group ─── */
const BADGE_COLORS = {
  streak:      { bg: 'linear-gradient(135deg, #FF9500, #FF5E3A)', ring: '#FF9500', dim: '#FFD6B0' },
  completions: { bg: 'linear-gradient(135deg, #7EA86C, #5C8748)', ring: '#7EA86C', dim: '#C4DFC0' },
  mastery:     { bg: 'linear-gradient(135deg, #CE82FF, #A855F7)', ring: '#CE82FF', dim: '#E8D0FF' },
  time:        { bg: 'linear-gradient(135deg, #1CB0F6, #0284C7)', ring: '#1CB0F6', dim: '#B8E4FB' },
  forest:      { bg: 'linear-gradient(135deg, #2DD4BF, #0D9488)', ring: '#2DD4BF', dim: '#B2F0E8' },
  engagement:  { bg: 'linear-gradient(135deg, #FF6B8A, #E11D48)', ring: '#FF6B8A', dim: '#FFD0DA' },
};

const Achievements = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user: contextUser, isPremium, isAdmin, isBetatester, token, loading: userLoading } = useUser();
  const { setHeaderProps } = useHeader();
  const isIt = i18n.language === 'it';
  const isEs = i18n.language === 'es';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = contextUser?.user_id || contextUser?.id;
  const isFreeDebug = contextUser?.role === 'free-debug' || contextUser?.subscription_status === 'free-debug';
  const canAccess = isPremium || isAdmin || isBetatester || isFreeDebug;

  const handleNotificationClick = useCallback((action) => {
    if (action === 'suggestion' || action === 'checkin') navigate('/dashboard');
    else if (action === 'events') navigate('/events');
    else if (action === 'subscription') navigate('/subscription');
    else if (action === 'achievements') navigate('/achievements');
  }, [navigate]);

  useEffect(() => {
    setHeaderProps({
      title: isIt ? 'Traguardi' : isEs ? 'Logros' : 'Achievements',
      user: contextUser,
      userId,
      onNotificationClick: handleNotificationClick,
    });
  }, [isIt, isEs, contextUser, userId, setHeaderProps, handleNotificationClick]);

  const fetchAchievements = useCallback(async () => {
    if (!userId || !canAccess) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || token;
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await axios.get(`${API}/achievements/${userId}`, { headers });
      setData(res.data);
    } catch (e) {
      console.error('[Achievements] fetch error:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [userId, canAccess, token]);

  useEffect(() => { fetchAchievements(); }, [fetchAchievements]);

  // If user context finished loading but no userId yet, stop the spinner
  useEffect(() => {
    if (!userLoading && !userId) setLoading(false);
  }, [userLoading, userId]);

  /* ─── Premium gate ─── */
  if (!userLoading && !canAccess) {
    return (
      <div style={{ minHeight: 'calc(100dvh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 24px', boxSizing: 'border-box' }}>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <Icon icon="solar:lock-linear" style={{ display: 'block', width: 48, height: 48, color: 'var(--c-texSec)', margin: '0 auto 16px' }} />
          <div style={{ fontFamily: 'Manrope-Variable', fontSize: 16, fontWeight: 600, color: 'var(--c-texPri)', marginBottom: 8 }}>
            {isIt ? 'Funzione Premium' : isEs ? 'Función Premium' : 'Premium Feature'}
          </div>
          <div style={{ fontFamily: 'Manrope-Variable', fontSize: 13, color: 'var(--c-texSec)' }}>
            {isIt ? 'Passa a Premium per sbloccare i traguardi' : isEs ? 'Pasa a Premium para desbloquear los logros' : 'Upgrade to Premium to unlock achievements'}
          </div>
        </div>
      </div>
    );
  }

  if (loading || userLoading || !data) {
    return <AchievementsSkeleton />;
  }

  const achievements = data.achievements || [];
  const unlocked = achievements.filter(a => a.is_unlocked);
  const locked = achievements
    .filter(a => !a.is_unlocked)
    .sort((a, b) => {
      const ratioA = (a.current || 0) / (a.threshold || 1);
      const ratioB = (b.current || 0) / (b.threshold || 1);
      return ratioB - ratioA; // closest to unlock first
    });

  // Group locked achievements by group
  const lockedByGroup = {};
  locked.forEach(a => {
    if (!lockedByGroup[a.group]) lockedByGroup[a.group] = [];
    lockedByGroup[a.group].push(a);
  });

  return (
    <div style={{ margin: 0, fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif', boxSizing: 'border-box' }}>
      <style>{`
        @keyframes achAlmostPulse {
          0%, 100% { opacity: 0.85; }
          50%      { opacity: 1; }
        }
      `}</style>
      <div style={{ maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', padding: '12px 16px 40px' }}>

        {/* ═══ No achievements registered yet ═══ */}
        {achievements.length === 0 ? (
          <div style={{ paddingTop: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--c-surface-card, #f5f3ef)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Icon icon="solar:cup-star-linear" style={{ width: 28, height: 28, color: '#bbb' }} />
            </div>
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 600, color: 'var(--c-texPri)', marginBottom: 6 }}>
              {isIt ? 'Nessun traguardo ancora' : isEs ? 'Sin logros todavía' : 'No achievements yet'}
            </div>
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 13, color: 'var(--c-texSec)', lineHeight: 1.5 }}>
              {isIt ? 'Inizia a completare gesti per sbloccare i tuoi primi traguardi' : isEs ? 'Comienza a completar gestos para desbloquear tus primeros logros' : 'Start completing gestures to unlock your first achievements'}
            </div>
          </div>
        ) : (
          <>
            {/* ═══ Summary pill ═══ */}
            <div style={{
              textAlign: 'center', padding: '8px 0 16px',
            }}>
              <span style={{
                fontFamily: 'Manrope-Variable', fontSize: 13, fontWeight: 600,
                color: 'var(--c-texSec)',
              }}>
                {unlocked.length}/{achievements.length} {isIt ? 'sbloccati' : isEs ? 'desbloqueados' : 'unlocked'}
              </span>
            </div>

            {/* ═══ Unlocked badges — cards with dividers ═══ */}
            {unlocked.length > 0 ? (
          <div style={{
            borderRadius: 20, overflow: 'hidden',
            background: 'var(--c-surface-card)',
            border: '1px solid var(--c-border)',
          }}>
            {unlocked.map((ach, idx) => {
              const title = isEs ? (ach.title_es || ach.title_en) : isIt ? ach.title_it : ach.title_en;
              const desc = isEs ? (ach.desc_es || ach.desc_en) : isIt ? ach.desc_it : ach.desc_en;
              const colors = BADGE_COLORS[ach.group] || BADGE_COLORS.completions;
              return (
                <div key={ach.key}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '16px 18px',
                    }}
                  >
                    <div style={{
                      width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: colors.bg,
                      boxShadow: `0 4px 14px ${colors.ring}50, inset 0 -3px 0 rgba(0,0,0,0.18), inset 0 2px 0 rgba(255,255,255,0.3)`,
                      border: `3px solid ${colors.ring}`,
                    }}>
                      <Icon icon={ach.icon} style={{ width: 24, height: 24, color: '#fff' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'Manrope-Variable', fontSize: 14, fontWeight: 700, color: 'var(--c-texPri)' }}>
                        {title}
                      </div>
                      <div style={{ fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 400, color: 'var(--c-texSec)', marginTop: 2 }}>
                        {desc}
                      </div>
                    </div>
                    <Icon icon="solar:check-circle-bold" style={{ width: 22, height: 22, color: colors.ring, flexShrink: 0, filter: 'drop-shadow(0 0 4px ' + colors.ring + '60)' }} />
                  </div>
                  {idx < unlocked.length - 1 && (
                    <div style={{ height: 1, background: 'var(--c-border)', margin: '0 18px 0 84px' }} />
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Icon icon="solar:cup-star-linear" style={{ width: 36, height: 36, color: 'var(--c-texTer)', marginBottom: 12, display: 'block' }} />
            <div style={{ fontFamily: 'Manrope-Variable', fontSize: 13, color: 'var(--c-texTer)', lineHeight: 1.5 }}>
              {isIt ? 'Completa gesti per sbloccare i tuoi primi traguardi' : isEs ? 'Completa gestos para desbloquear tus primeros logros' : 'Complete gestures to unlock your first achievements'}
            </div>
          </div>
        )}

            {/* ═══ Locked achievements — grouped with progress bars ═══ */}
            {locked.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <div style={{
                  fontFamily: 'Manrope-Variable', fontSize: 13, fontWeight: 600,
                  color: 'var(--c-texTer)', letterSpacing: '0.06em', textTransform: 'uppercase',
                  marginBottom: 12, paddingLeft: 4,
                }}>
                  {isIt ? 'Prossimi' : isEs ? 'Próximos' : 'Coming Up'}
                </div>

                {GROUP_ORDER.map(groupKey => {
                  const groupAchs = lockedByGroup[groupKey];
                  if (!groupAchs || groupAchs.length === 0) return null;
                  const groupLabel = isIt ? GROUP_LABELS[groupKey]?.it : isEs ? GROUP_LABELS[groupKey]?.es : GROUP_LABELS[groupKey]?.en;

                  return (
                    <div key={groupKey} style={{ marginBottom: 16 }}>
                      {/* Group header */}
                      <div style={{
                        fontFamily: 'Manrope-Variable', fontSize: 11, fontWeight: 600,
                        color: 'var(--c-texTer)', marginBottom: 8, paddingLeft: 4,
                      }}>
                        {groupLabel}
                      </div>

                      <div style={{
                        borderRadius: 16, overflow: 'hidden',
                        background: 'var(--c-surface-card)',
                        border: '1px solid var(--c-border)',
                      }}>
                        {groupAchs.map((ach, idx) => {
                          const title = isEs ? (ach.title_es || ach.title_en) : isIt ? ach.title_it : ach.title_en;
                          const colors = BADGE_COLORS[ach.group] || BADGE_COLORS.completions;
                          const progress = ach.threshold > 0 ? Math.min((ach.current || 0) / ach.threshold, 1) : 0;
                          const progressPct = Math.round(progress * 100);
                          const isAlmost = progress >= 0.8;

                          return (
                            <div key={ach.key}>
                              <div
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 14,
                                  padding: '14px 18px',
                                  animation: isAlmost ? 'achAlmostPulse 2s ease-in-out infinite' : 'none',
                                }}
                              >
                                {/* Dimmed badge */}
                                <div style={{
                                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: colors.dim,
                                  border: `2px solid ${colors.dim}`,
                                  opacity: 0.7,
                                }}>
                                  <Icon icon={ach.icon} style={{ width: 20, height: 20, color: colors.ring, opacity: 0.5 }} />
                                </div>

                                {/* Title + progress bar */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{
                                    fontFamily: 'Manrope-Variable', fontSize: 13, fontWeight: 600,
                                    color: 'var(--c-texSec)',
                                  }}>
                                    {title}
                                  </div>
                                  {/* Progress bar */}
                                  <div style={{
                                    marginTop: 6, height: 6, borderRadius: 3,
                                    background: 'var(--c-border)',
                                    overflow: 'hidden',
                                  }}>
                                    <div style={{
                                      width: `${progressPct}%`,
                                      height: '100%', borderRadius: 3,
                                      background: isAlmost ? colors.ring : `${colors.ring}80`,
                                      transition: 'width 0.5s ease',
                                    }} />
                                  </div>
                                  <div style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    marginTop: 3,
                                  }}>
                                    <span style={{
                                      fontFamily: 'Manrope-Variable', fontSize: 10, fontWeight: 500,
                                      color: isAlmost ? colors.ring : 'var(--c-texTer)',
                                    }}>
                                      {ach.current || 0}/{ach.threshold}
                                    </span>
                                    {isAlmost && (
                                      <span style={{
                                        fontFamily: 'Manrope-Variable', fontSize: 10, fontWeight: 700,
                                        color: colors.ring,
                                      }}>
                                        {isIt ? 'Quasi fatto!' : isEs ? '¡Casi listo!' : 'Almost there!'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {idx < groupAchs.length - 1 && (
                                <div style={{ height: 1, background: 'var(--c-border)', margin: '0 18px 0 76px' }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Achievements;
